// AyurMa white-label report copy — English is the source of truth.
//
// This file defines the contract: `AyurmaStrings` is derived from it, so adding
// a string here makes ru.ts and zh.ts fail to compile until they are filled in.
// `npx tsc --noEmit` is therefore the complete missing-translation test.
//
// Two rules to preserve that guarantee:
//   1. No `as const` — it would bake in literal types and force ru/zh to repeat
//      the English strings verbatim.
//   2. Never use arrays for sets of entries (glossary terms, severity bands,
//      movement rows). `typeof someArray` widens to T[] and loses arity, so a
//      translator dropping an entry would still compile. Key them by code.

export const ayurmaEn = {
  /** Right-hand footer label, per page. */
  sections: {
    coverResults: "Metabolic Assessment",
    caloricNutrition: "Caloric Balance & Nutrition",
    nutritionGuide: "Nutrition Guide",
    movementPlan: "Movement Plan",
    sourcesDisclaimer: "Scientific Sources",
  },

  cover: {
    title: "Metabolic Assessment",
    subtitle: "Resting Metabolic Rate (RMR) Report",
    resultsHeading: "Your Results",
  },

  patientInfo: {
    name: "Name",
    testType: "Test Type",
    /** Fixed: this white-label report is RMR-only. */
    testTypeValue: "Resting Assessment (RMR)",
    gender: "Gender",
    date: "Date",
    weight: "Weight",
    activityLevel: "Activity Level",
    height: "Height",
    goal: "Goal",

    /**
     * Units for the weight / height values. Which pair is used is decided by the
     * payload's measurement-unit codes, not by the language — a client testing in
     * metric gets kg/cm on the English report too.
     */
    units: {
      kg: "kg",
      lbs: "lbs",
      cm: "cm",
      ft: "ft",
      in: "in",
    },

    /** Values of CUSTOM_DATA.gender, keyed by the code the questionnaire uses. */
    genders: {
      male: "Male",
      female: "Female",
    },

    /**
     * CUSTOM_DATA.exercise_frequency is sessions per week, 0-7. Only the zero case
     * has its own wording; the rest is one template so a translator controls word
     * order and the "times" plural instead of getting seven near-identical strings.
     */
    exerciseFrequency: {
      none: "None",
      perWeek: "{count}x / week",
    },

    /** Values of CUSTOM_DATA.primary_fitness_goal, keyed by questionnaire code. */
    goals: {
      muscleDevelopment: "Muscle development",
      strengthDevelopment: "Strength development",
      endurancePerformance: "Performance for Endurance Sports",
      longevity: "Longevity",
      bodyRecomposition: "Body recomposition",
      dynamicPerformance: "Performance for Dynamic Sports",
    },
  },

  glossary: {
    heading: "Key Metrics (Resting)",
    terms: {
      RER: {
        term: "RER",
        text: "Respiratory Exchange Ratio — indicates whether carbs or fat are being used for fuel",
      },
      VO2: {
        term: "VO2",
        text: "Fat oxidation — fat utilization for energy at rest",
      },
      VCO2: {
        term: "VCO2",
        text: "Carbohydrate oxidation — carbohydrate utilization for energy at rest",
      },
      VT: {
        term: "VT",
        text: "Tidal Volume — volume of air moved in and out during a single breath",
      },
      BF: {
        term: "BF",
        text: "Breathing Frequency — number of breaths per minute",
      },
      VE: {
        term: "VE",
        text: "Minute Ventilation — volume of air moved in and out per minute",
      },
    },
  },

  /**
   * Per-metric name and one-line explainer.
   *
   * Both are frontend-owned: the payload's TITLE1 is English-only, and its
   * HOW_IT_IS_MEASURED_TEXT is a full clinical paragraph rather than a caption.
   * Names follow the client's mock-up. Keyed by name; METRIC_KEYS maps METRIC_TYPE
   * codes onto these, and an unmapped metric falls back to the payload title.
   */
  metrics: {
    ventilationEfficiency: {
      name: "Ventilation Efficiency",
      description:
        "Your lungs' ability to absorb oxygen and clear carbon dioxide",
    },
    breathingCoordination: {
      name: "Breathing Coordination",
      description:
        "Your ability to maintain regular and efficient breathing during resting state",
    },
    fatBurningEfficiency: {
      name: "Fat Burning Efficiency",
      description: "Your cells' ability to use fat as a fuel source",
    },
    metabolicRate: {
      name: "Metabolic Rate",
      description: "Calories your body burns at rest",
    },
    metabolicFlexibility: {
      name: "Metabolic Flexibility",
      description:
        "Your ability to rapidly switch between fat and carbohydrates as fuel sources",
    },
    sympatheticParasympathetic: {
      name: "Sympathetic / Parasympathetic Activation",
      description:
        "The balance between the two main parts of your autonomic nervous system",
    },
    lungUtilization: {
      name: "Lung Utilization",
      description: "How much of your lung capacity you use in a resting state",
    },
    heartRateVariability: {
      name: "Heart Rate Variability (HRV)",
      description: "Your cardiovascular system's function in resting conditions",
    },
    // Descriptions still to be supplied by the client. An empty string means
    // "not written yet" — the score bar omits the line rather than showing a gap.
    oxygenDelivery: {
      name: "Oxygen Delivery",
      description: "",
    },
    respiratoryCapacity: {
      name: "Respiratory Capacity",
      description: "",
    },
  },

  severity: {
    howToRead: "How to read your scores",
    /** End labels for the Low-to-High range bar (metabolic flexibility). */
    range: { low: "Low", high: "High" },
    /**
     * Scale labels printed under each score bar. Worded differently from the
     * legend chips above (which are the short uppercase forms), and keyed to the
     * same SEVERITY_SCALE bands.
     */
    ticks: {
      severe: "Severe limitation",
      limitation: "Limitation",
      neutral: "Neutral",
      good: "Good",
      excellent: "Excellent",
    },
    // Must match the boundaries in SEVERITY_SCALE (<=20 / <=40 / <=60 / <=80),
    // which in turn mirror ChartBars.tsx in the default report.
    bands: {
      severe: { label: "SEVERE", range: "0–20%" },
      limitation: { label: "LIMITATION", range: "21–40%" },
      neutral: { label: "NEUTRAL", range: "41–60%" },
      good: { label: "GOOD", range: "61–80%" },
      excellent: { label: "EXCELLENT", range: "81–100%" },
    },
  },

  caloric: {
    heading: "Caloric Balance",
    // The unit lives in the header so it is stated once per card.
    burnTitle: "CALORIES YOU BURN (KCAL/DAY)",
    eatTitle: "CALORIES YOU SHOULD EAT (KCAL/DAY)",
    trainingDays: "Training days",
    routineDays: "Routine activity days",
  },

  macros: {
    heading: "Macronutrient Diary",
    /** Keyed by the GRAPH entry INDEX the payload uses: 1 / 2 / 3. */
    names: { protein: "Protein", carbs: "Carbs", fat: "Fat" },
    /** Re-attached to the numbers parsed out of the payload's KCAL / G strings. */
    units: { kcal: "kcal", g: "g" },
    trainingDays: "Training Days",
    routineDays: "Routine Activity Days",
  },

  recommendations: {
    heading: "Recommendations",
    rows: {
      mealFrequency: "Meal Frequency",
      timing: "Timing",
      hydration: "Hydration",
      infusions: "Infusions",
      aroma: "Aroma",
    },
  },

  nutritionGuide: {
    heading: "Nutrition Guide",
    subtitle: "Personalized by your AyurMa practitioner.",
    eatLimitAvoidHeading: "Eat / Limit / Avoid",
    columns: {
      eat: "EAT",
      limit: "LIMIT",
      avoid: "AVOID",
    },
    otherRecommendations: "Other Recommendations",
    supplements: "Supplements",
  },

  movement: {
    heading: "Movement Plan",
    subtitle: "Two days per week for each category.",
    yoga: {
      label: "Yoga",
      hint: "Write in prescribed postures, repetitions, and practices.",
      columns: {
        posture: "POSTURE / PRACTICE",
        reps: "REPS / DURATION",
        notes: "NOTES",
      },
    },
    aerobic: {
      label: "AEROBIC ACTIVITIES",
      rows: {
        briskWalking: "Brisk Walking",
        swimming: "Swimming",
        cycling: "Cycling",
        other: "Other",
      },
    },
    strength: {
      label: "STRENGTH TRAINING",
      rows: {
        floorExercises: "Floor Exercises",
        calisthenics: "Calisthenics",
        freeWeights: "Free Weights",
        other: "Other",
      },
    },
  },

  footer: {
    /**
     * Left-hand footer label. {company} comes from the theme so the brand name is
     * never translated, while the descriptor beside it localizes. Translators also
     * control the separator and word order.
     */
    brandLabel: "{company} · Metabolic Wellness",
  },

  qr: {
    caption:
      "Scan to learn more about your Sympathetic / Parasympathetic activation",
  },

  sources: {
    heading: "Scientific Sources",
    /**
     * Citations stay in the language of publication per Vancouver/ICMJE style —
     * translating a title would stop it identifying the paper. This note tells
     * the reader that is deliberate, so it is shown only on the ru/zh reports; on
     * the English one the citations are already in the reader's language. The key
     * still has to exist here, since this file is the type contract.
     */
    originalLanguageNote:
      "References are listed in their original language of publication.",
    disclaimerLabel: "Disclaimer:",
    /**
     * Frontend-owned so it localizes with the rest of the report; the backend
     * only ever served this in English. {company} is replaced with the theme's
     * companyName. Legal copy — have AyurMa confirm the ru/zh wording.
     */
    disclaimerBody:
      "The present Assessment/Report is intended for information purposes only and under no circumstances should it be considered a substitute for professional medical advice, diagnosis or treatment. You need to consult your physician and/or family doctor prior to engaging in any exercise program and/or changing your diet and/or habits as a result of the information provided by the present Assessment/Report. {company} makes no representation that the present Assessment/Report will result in any improvement of your health and fitness status. You agree that participating in any workout regimen, physical exercise or activity may result in an increased risk of physical injury based on the nature, frequency, intensity and duration of the workout regimen, physical exercise or activity. You agree that if you participate in any workout regimen, physical exercise or activity, you do so at your own risk and you assume the risk of any and all injury and/or damage you may suffer.",
  },

  /**
   * Page chrome rather than report copy — the on-screen language switcher, which
   * is never part of the exported PDF. The label is only ever read aloud (it is
   * the group's accessible name); the language names beside it are autonyms and
   * live in the component, since they are the same in every dictionary.
   */
  langPicker: {
    label: "Report language",
  },
};

/** The contract ru.ts and zh.ts are checked against. */
export type AyurmaStrings = typeof ayurmaEn;
